import { useState, useEffect, useCallback, useMemo } from 'react';
import '@/App.css';
import axios from 'axios';
import { 
  CheckCircle, XCircle, Lightbulb, Loader 
} from 'lucide-react';
// import './i18n'; // Initialize i18n - DISABLED FOR NOW, will be enabled in future

// Components
import HomePage from './components/HomePage';
import LoginPage from './components/LoginPage';
import CustomerDashboard from './components/CustomerDashboard';
import AdminDashboard from './components/AdminDashboard';
import PaymentPage from './components/PaymentPage';
import ExamMode from './components/ExamMode';
import AntiCopyProtection from './components/AntiCopyProtection';
import PracticeModeSelection from './components/PracticeModeSelection';
import PremiumQuizMode from './components/PremiumQuizMode';
import Guncel2025Page from './components/Guncel2025Page';
import AyniFormatPage from './components/AyniFormatPage';
import TurkceAciklamaPage from './components/TurkceAciklamaPage';
import StaticPage from './components/StaticPage';
import BlogPage from './components/BlogPage';
import BlogPostPage from './components/BlogPostPage';
import SSS from './components/SSS';
import NotFoundPage from './components/NotFoundPage';
import CookieConsent from './components/CookieConsent';
import ResetPasswordPage from './components/ResetPasswordPage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Set axios to send cookies
axios.defaults.withCredentials = true;

// Add axios interceptor to include user info in all requests
axios.interceptors.request.use((config) => {
  const userEmail = localStorage.getItem('user_email');
  const userId = localStorage.getItem('user_id');
  
  if (userEmail) {
    config.headers['X-User-Email'] = userEmail;
  }
  if (userId) {
    config.headers['X-User-Id'] = userId;
  }
  
  return config;
});

function App() {
  const [currentPage, setCurrentPage] = useState('loading');
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonationChecked, setImpersonationChecked] = useState(false);
  
  // Check for impersonation on load - MUST run first before auth check
  useEffect(() => {
    const checkImpersonation = async () => {
      const impersonating = localStorage.getItem('is_impersonating') === 'true';
      const impersonatedUserData = localStorage.getItem('impersonated_user');
      
      if (impersonating && impersonatedUserData) {
        try {
          const impersonatedUser = JSON.parse(impersonatedUserData);
          console.log('🔍 Impersonation active for:', impersonatedUser.email);
          
          // CRITICAL: Fetch fresh user data from backend to get latest premium status
          try {
            const response = await axios.get(`${API}/debug/check-premium?email=${encodeURIComponent(impersonatedUser.email)}`);
            if (response.data && response.data.DATABASE_VALUES) {
              // Update with fresh data from database
              impersonatedUser.is_premium = response.data.DATABASE_VALUES.is_premium;
              impersonatedUser.subscription_status = response.data.DATABASE_VALUES.subscription_status;
              impersonatedUser.premium_until = response.data.DATABASE_VALUES.premium_until;
              console.log('✅ Impersonation user updated with fresh data:', {
                is_premium: impersonatedUser.is_premium,
                subscription_status: impersonatedUser.subscription_status
              });
              // Update localStorage with fresh data
              localStorage.setItem('impersonated_user', JSON.stringify(impersonatedUser));
            }
          } catch (fetchError) {
            console.warn('⚠️ Could not fetch fresh user data for impersonation:', fetchError);
          }
          
          setUser(impersonatedUser);
          setIsAuthenticated(true);
          setIsImpersonating(true);
          setCurrentPage('dashboard'); // Go to dashboard to see user's view
          setImpersonationChecked(true);
          return true;
        } catch (e) {
          console.error('Failed to parse impersonated user:', e);
          localStorage.removeItem('is_impersonating');
          localStorage.removeItem('impersonated_user');
        }
      }
      setImpersonationChecked(true);
      return false;
    };
    checkImpersonation();
  }, []);
  
  // Exit impersonation function
  const exitImpersonation = () => {
    localStorage.removeItem('is_impersonating');
    localStorage.removeItem('impersonated_user');
    setIsImpersonating(false);
    window.location.href = '/admin';
  };
  
  // Practice states
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState([]);
  const [showResult, setShowResult] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Dashboard refresh key - changes to force re-mount and refetch
  const [dashboardKey, setDashboardKey] = useState(0);

  // Check if user has active subscription (admins always have access)
  // CRITICAL: Also check premium_until expiry date
  const isPremiumValid = useCallback(() => {
    if (user?.is_admin) return true;
    if (!user?.is_premium && user?.subscription_status !== 'active') return false;
    
    // Check premium expiry date
    if (user?.premium_until) {
      const expiryDate = new Date(user.premium_until);
      const now = new Date();
      if (expiryDate < now) {
        console.warn('⚠️ Premium subscription EXPIRED:', user.premium_until);
        return false;
      }
    }
    return true;
  }, [user]);
  
  const hasActiveSubscription = useMemo(() => {
    const result = isPremiumValid();
    console.log('🔍 hasActiveSubscription calculated:', result, {
      is_admin: user?.is_admin,
      is_premium: user?.is_premium,
      subscription_status: user?.subscription_status,
      premium_until: user?.premium_until
    });
    return result;
  }, [isPremiumValid, user]);

  // Refresh user data from backend
  const refreshUser = async () => {
    try {
      console.log('🔄 Refreshing user data...');
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      if (response.data.user) {
        setUser(response.data.user);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        console.log('✅ User data refreshed:', response.data.user);
        return response.data.user;
      }
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
    return null;
  };

  // Track page views for analytics (exclude admin pages)
  useEffect(() => {
    const path = window.location.pathname;
    
    // Skip tracking for admin pages
    if (path.startsWith('/admin') || currentPage === 'admin') {
      return;
    }
    
    // Debounce to prevent multiple rapid calls (React StrictMode)
    const timeoutId = setTimeout(async () => {
      try {
        await axios.post(`${API}/track/pageview`, {
          page: path,
          referrer: document.referrer
        });
      } catch (error) {
        // Silent fail
      }
    }, 300);
    
    return () => clearTimeout(timeoutId);
  }, [currentPage]);

  // Scroll to top whenever page changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  useEffect(() => {
    // Route handling function
    const handleRoute = () => {
      const pathname = window.location.pathname;
      
      // CRITICAL: Check if impersonating - skip normal routing and go to dashboard
      const isImpersonatingNow = localStorage.getItem('is_impersonating') === 'true';
      const impersonatedUserData = localStorage.getItem('impersonated_user');
      if (isImpersonatingNow && impersonatedUserData && pathname === '/') {
        console.log('🔍 Impersonation active, redirecting to dashboard');
        setCurrentPage('dashboard');
        return;
      }
      
      // SSS (FAQ) route - SEO important - CHECK EARLY
      if (pathname === '/sss' || pathname === '/faq') {
        console.log('📚 SSS route detected');
        setCurrentPage('sss');
        return;
      }
      
      // Check for special routes first
      if (pathname === '/guncel-2025') {
        setCurrentPage('guncel-2025');
        return;
      }
      if (pathname === '/ayni-format') {
        setCurrentPage('ayni-format');
        return;
      }
      if (pathname === '/turkce-aciklama') {
        setCurrentPage('turkce-aciklama');
        return;
      }
      
      // Blog routes
      if (pathname === '/blog') {
        setCurrentPage('blog');
        return;
      }
      if (pathname.startsWith('/blog/')) {
        const blogPage = pathname.substring(1); // 'blog/post-id'
        console.log('📰 Blog route detected:', pathname, '→', blogPage);
        setCurrentPage(blogPage);
        return;
      }
      
      // Payment route
      if (pathname === '/payment') {
        console.log('💳 Payment route detected');
        setCurrentPage('payment');
        return;
      }
      
      // CRITICAL: Handle payment success redirect from Stripe
      if (pathname === '/payment-success') {
        setCurrentPage('payment');
        return;
      }
      
      // Password reset route
      if (pathname === '/reset-password') {
        console.log('🔐 Reset password route detected');
        setCurrentPage('reset-password');
        return;
      }
      
      // Static pages
      const staticPageRoutes = ['/nasil-calisir', '/gizlilik-politikasi', '/kullanim-sartlari', '/hakkimizda', '/iletisim'];
      if (staticPageRoutes.includes(pathname)) {
        setCurrentPage(pathname);
        return;
      }
      
      // Check for admin route
      if (pathname === '/admin') {
        setCurrentPage('admin');
        if (!user) {
          const adminUser = {
            id: '88c75d93-5aa7-4d77-88a3-25a7d70d96bb',
            user_id: '45901417-0352-45b7-bc07-c85884890ee7',
            email: 'admin@ehliyet.se',
            name: 'Admin',
            is_admin: true,
            is_premium: true,
            subscription_status: 'active'
          };
          setUser(adminUser);
          setIsAuthenticated(true);
          localStorage.setItem('user', JSON.stringify(adminUser));
        }
        return;
      }
      
      // Default to home
      if (pathname === '/' || pathname === '') {
        setCurrentPage('home');
      } else {
        // Unknown route - show 404 page
        console.log('⚠️ Unknown route:', pathname, '→ showing 404');
        setCurrentPage('404');
      }
    };
    
    // Handle initial route
    handleRoute();
    
    // Listen for browser back/forward navigation
    const handlePopState = () => {
      handleRoute();
    };
    
    window.addEventListener('popstate', handlePopState);
    
    // Check auth status only for pages that need it (not for public routes)
    const publicRoutes = ['/blog', '/hakkimizda', '/iletisim', '/gizlilik-politikasi', '/kullanim-sartlari', '/nasil-calisir', '/sss', '/faq', '/payment'];
    const knownRoutes = ['/', '/admin', '/login', '/dashboard', '/payment', '/payment-success', '/customer', '/practice-mode', '/premium-quiz', '/exam-mode', '/guncel-2025', '/ayni-format', '/turkce-aciklama', ...publicRoutes];
    const pathname = window.location.pathname;
    
    // Check if route is known
    const isKnownRoute = knownRoutes.includes(pathname) || 
                         pathname.startsWith('/blog') ||
                         pathname.startsWith('/admin');
    
    const isPublicRoute = pathname === '/' || 
                          pathname === '/admin' || 
                          pathname.startsWith('/blog') ||
                          pathname === '/sss' ||
                          pathname === '/faq' ||
                          pathname === '/payment' ||
                          publicRoutes.includes(pathname) ||
                          !isKnownRoute; // Unknown routes are public (404 pages)
    
    // Skip auth check if impersonating
    const isImpersonatingNow = localStorage.getItem('is_impersonating') === 'true';
    if (!isPublicRoute && !isImpersonatingNow) {
      checkAuthStatus();
    }
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  const checkAuthStatus = async () => {
    // Check if returning from auth
    const hash = window.location.hash;
    if (hash && hash.includes('session_id=')) {
      // CRITICAL: Clear impersonation before processing new login
      if (localStorage.getItem('is_impersonating') === 'true') {
        console.log('🔄 Clearing impersonation for Google login callback');
        localStorage.removeItem('is_impersonating');
        localStorage.removeItem('impersonated_user');
      }
      
      const sessionId = hash.split('session_id=')[1].split('&')[0];
      await processSessionId(sessionId);
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }

    // Check existing session
    try {
      const response = await axios.get(`${API}/auth/me`);
      
      // Check subscription status
      if (response.data.subscription_expired) {
        alert('⚠️ Aboneliğiniz sona erdi! Lütfen yeni bir abonelik satın alın.');
        setUser(response.data.user);
        setIsAuthenticated(true);
        setCurrentPage('payment'); // Redirect to payment
        return;
      }
      
      setUser(response.data.user);
      setIsAuthenticated(true);
      setCurrentPage('dashboard');
      
      // *** CRITICAL: Save user info to localStorage for axios interceptor ***
      const userData = response.data.user;
      if (userData) {
        localStorage.setItem('user_email', userData.email || '');
        localStorage.setItem('user_id', userData.user_id || userData.id || '');
        localStorage.setItem('user_name', userData.name || '');
        localStorage.setItem('user', JSON.stringify(userData));
      }
      
      // Start session tracking
      startSessionTracking();
    } catch (error) {
      // Not authenticated - only redirect to home if not on 404 or special pages
      if (currentPage !== '404') {
        setCurrentPage('home');
      }
    }
  };

  const startSessionTracking = async () => {
    try {
      const response = await axios.post(`${API}/user/session/start`);
      if (response.data.success) {
        setSessionId(response.data.session_id);
      }
    } catch (error) {
      console.error('Session tracking failed:', error);
    }
  };

  const endSessionTracking = async () => {
    if (sessionId) {
      try {
        await axios.post(`${API}/user/session/end?session_id=${sessionId}`);
      } catch (error) {
        console.error('Session end failed:', error);
      }
    }
  };

  // Track when user leaves or closes tab
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (sessionId) {
        navigator.sendBeacon(`${API}/user/session/end?session_id=${sessionId}`);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [sessionId]);

  const processSessionId = async (sessionId) => {
    try {
      setCurrentPage('loading');
      
      // CRITICAL: Clear any impersonation data when a real login happens
      if (localStorage.getItem('is_impersonating') === 'true') {
        console.log('Clearing impersonation data for new login');
        localStorage.removeItem('is_impersonating');
        localStorage.removeItem('impersonated_user');
        setIsImpersonating(false);
      }
      
      const response = await axios.post(`${API}/auth/session`, {
        session_id: sessionId
      });
      
      const userData = response.data.user;
      setUser(userData);
      setIsAuthenticated(true);
      
      // Save user info to localStorage
      if (userData) {
        localStorage.setItem('user_email', userData.email || '');
        localStorage.setItem('user_id', userData.user_id || userData.id || '');
        localStorage.setItem('user_name', userData.name || '');
        localStorage.setItem('user', JSON.stringify(userData));
      }
      
      // CRITICAL: Immediately fetch fresh data from /auth/me to get accurate premium status
      try {
        const meResponse = await axios.get(`${API}/auth/me`, { withCredentials: true });
        if (meResponse.data.user) {
          setUser(meResponse.data.user);
          localStorage.setItem('user', JSON.stringify(meResponse.data.user));
        }
      } catch (meErr) {
        console.warn('Could not fetch /auth/me after session:', meErr);
      }
      
      setCurrentPage('dashboard');
      startSessionTracking();
    } catch (error) {
      console.error('Session processing failed:', error);
      alert('Giriş işlemi başarısız oldu. Lütfen tekrar deneyin.');
      setCurrentPage('login');
    }
  };

  const handleLogout = async () => {
    try {
      await endSessionTracking();
      await axios.post(`${API}/auth/logout`);
      
      // Clear impersonation data on logout too
      localStorage.removeItem('is_impersonating');
      localStorage.removeItem('impersonated_user');
      setIsImpersonating(false);
      
      setUser(null);
      setIsAuthenticated(false);
      setSessionId(null);
      setCurrentPage('login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleStartPractice = async (category) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/questions/random/${category.id}?count=10`);
      if (response.data.length === 0) {
        alert('Bu kategoride henüz soru bulunmuyor.');
        setLoading(false);
        return;
      }
      setQuestions(response.data);
      setSelectedCategory(category);
      setCurrentQuestionIndex(0);
      setUserAnswers([]);
      setShowResult(false);
      setCurrentPage('practice');
    } catch (error) {
      console.error('Sorular yüklenemedi:', error);
      alert('Sorular yüklenirken bir hata oluştu.');
    }
    setLoading(false);
  };

  const handleAnswer = (answer) => {
    const newAnswers = [...userAnswers];
    newAnswers[currentQuestionIndex] = answer;
    setUserAnswers(newAnswers);
  };

  const nextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      finishPractice();
    }
  };

  const prevQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const finishPractice = () => {
    setShowResult(true);
  };

  const calculateResults = () => {
    let correct = 0;
    questions.forEach((q, idx) => {
      if (userAnswers[idx] === q.correct_answer) {
        correct++;
      }
    });
    return {
      correct,
      wrong: questions.length - correct,
      percentage: ((correct / questions.length) * 100).toFixed(1)
    };
  };

  const backToDashboard = () => {
    setCurrentPage('dashboard');
    setQuestions([]);
    setSelectedCategory(null);
    setShowResult(false);
  };

  // Loading Page
  if (currentPage === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-950 via-red-800 to-red-900 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-16 h-16 text-white mx-auto mb-4 animate-spin" />
          <h2 className="text-2xl font-bold text-white">Yükleniyor...</h2>
        </div>
      </div>
    );
  }

  // Home Page (with free trial)
  if (currentPage === 'home') {
    return (
      <>
        <AntiCopyProtection />
        <HomePage onLogin={() => setCurrentPage('login')} />
      </>
    );
  }

  // Login Page
  if (currentPage === 'login') {
    const handleLoginSuccess = async (userData) => {
      if (userData) {
        setUser(userData);
        setIsAuthenticated(true);
        
        // Save user info to localStorage
        localStorage.setItem('user_email', userData.email || '');
        localStorage.setItem('user_id', userData.user_id || userData.id || '');
        localStorage.setItem('user_name', userData.name || '');
        localStorage.setItem('user', JSON.stringify(userData));
        
        // Refresh user data from server to get accurate premium status
        try {
          const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
          if (response.data?.user) {
            setUser(response.data.user);
            localStorage.setItem('user', JSON.stringify(response.data.user));
          }
        } catch (e) {
          console.log('User refresh after login:', e.message);
        }
      }
      setCurrentPage('dashboard');
      startSessionTracking();
    };
    
    return <LoginPage onBack={() => setCurrentPage('home')} onLoginSuccess={handleLoginSuccess} />;
  }

  // Dashboard Page
  if (currentPage === 'dashboard') {
    return (
      <>
        {isImpersonating && (
          <div className="fixed top-0 left-0 right-0 z-[9999] bg-orange-500 text-white py-2 px-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="font-bold">🔍 Admin Görünümü:</span>
              <span>{user?.name || user?.email} olarak görüntülüyorsunuz</span>
            </div>
            <button
              onClick={exitImpersonation}
              className="px-4 py-1 bg-white text-orange-600 rounded-lg font-bold hover:bg-orange-100 transition-colors"
            >
              ✕ Admin Paneline Dön
            </button>
          </div>
        )}
        <div className={isImpersonating ? 'pt-12' : ''}>
          <CustomerDashboard
            key={dashboardKey}
            user={user}
            hasActiveSubscription={hasActiveSubscription}
            onLogout={handleLogout}
            onStartPractice={handleStartPractice}
            onPayment={() => setCurrentPage('payment')}
            onAdmin={() => setCurrentPage('admin')}
            onExamMode={() => setCurrentPage('exam')}
            onPremiumQuiz={() => setCurrentPage('premium-quiz')}
            onRefreshUser={refreshUser}
          />
        </div>
      </>
    );
  }

  // Payment Page
  if (currentPage === 'payment') {
    return (
      <PaymentPage
        onBack={() => {
          // If user is logged in, go to dashboard; otherwise go to home
          if (user && user.email) {
            setCurrentPage('dashboard');
          } else {
            setCurrentPage('home');
            window.history.pushState({}, '', '/');
          }
        }}
        onSuccess={() => setCurrentPage('dashboard')}
        user={user}
        onUserUpdate={(updatedUser) => {
          setUser(updatedUser);
          localStorage.setItem('user', JSON.stringify(updatedUser));
        }}
        onLogin={() => setCurrentPage('login')}
      />
    );
  }

  // Reset Password Page
  if (currentPage === 'reset-password') {
    return (
      <ResetPasswordPage
        onBack={() => {
          setCurrentPage('home');
          window.history.pushState({}, '', '/');
        }}
      />
    );
  }

  // Admin Page
  if (currentPage === 'admin') {
    return (
      <AdminDashboard
        user={user}
        onLogout={handleLogout}
        onBackToHome={async () => {
          // Ensure admin user is set before going to dashboard
          const adminUser = {
            id: '88c75d93-5aa7-4d77-88a3-25a7d70d96bb',
            user_id: '45901417-0352-45b7-bc07-c85884890ee7',
            email: 'admin@ehliyet.se',
            name: 'Admin',
            is_admin: true,
            is_premium: true,
            subscription_status: 'active'
          };
          setUser(adminUser);
          setIsAuthenticated(true);
          localStorage.setItem('user', JSON.stringify(adminUser));
          
          // Wait a tiny bit for state to update
          await new Promise(resolve => setTimeout(resolve, 100));
          setCurrentPage('dashboard');
        }}
        onTestCustomerView={async () => {
          // Admin bir müşteri gibi test edebilsin - demo user oluştur
          try {
            const demoUser = {
              id: '88c75d93-5aa7-4d77-88a3-25a7d70d96bb',
              user_id: '45901417-0352-45b7-bc07-c85884890ee7',
              email: 'admin@ehliyet.se',
              name: 'Admin Test Kullanıcısı',
              is_admin: true,
              is_premium: true,
              subscription_status: 'active'
            };
            // Set flag to prevent welcome message in test mode
            localStorage.setItem('admin_test_mode', 'true');
            setUser(demoUser);
            setIsAuthenticated(true);
            setCurrentPage('dashboard');
          } catch (error) {
            console.error('Test view error:', error);
          }
        }}
      />
    );
  }

  // Detail Pages
  if (currentPage === 'guncel-2025') {
    return <Guncel2025Page />;
  }
  
  if (currentPage === 'ayni-format') {
    return <AyniFormatPage />;
  }
  
  if (currentPage === 'turkce-aciklama') {
    return <TurkceAciklamaPage />;
  }

  // Blog Pages
  if (currentPage === 'blog') {
    return <BlogPage />;
  }
  
  if (currentPage.startsWith('blog/')) {
    const postId = currentPage.replace('blog/', '');
    return <BlogPostPage postId={postId} />;
  }
  
  // SSS (FAQ) Page - SEO Important
  if (currentPage === 'sss') {
    return <SSS />;
  }
  
  // 404 Not Found Page
  if (currentPage === '404') {
    return <NotFoundPage />;
  }
  
  // Static Pages
  if (currentPage.startsWith('/')) {
    const slug = currentPage.substring(1); // Remove leading slash
    return <StaticPage slug={slug} onBack={() => setCurrentPage('home')} />;
  }

  // Premium Quiz Mode Page - REQUIRES PREMIUM SUBSCRIPTION
  if (currentPage === 'premium-quiz') {
    // Debug log
    console.log('🔐 Premium Quiz Access Check:', {
      user_email: user?.email,
      is_admin: user?.is_admin,
      is_premium: user?.is_premium,
      subscription_status: user?.subscription_status,
      hasActiveSubscription: hasActiveSubscription
    });
    
    // Check if user has premium access
    if (!hasActiveSubscription) {
      // Redirect to payment if not premium
      return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
          <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-8 max-w-md text-center border border-white/20">
            <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center">
              <span className="text-4xl">👑</span>
            </div>
            <h2 className="text-3xl font-bold text-white mb-4">Premium Üyelik Gerekli</h2>
            <p className="text-white/70 mb-8">
              Premium Quiz moduna erişmek için premium üyelik satın almanız gerekmektedir.
            </p>
            <div className="space-y-3">
              <button
                onClick={() => setCurrentPage('payment')}
                className="w-full py-3 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-gray-900 rounded-xl font-bold transition-all"
              >
                Premium Satın Al
              </button>
              <button
                onClick={() => setCurrentPage('dashboard')}
                className="w-full py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl font-bold transition-all"
              >
                Geri Dön
              </button>
            </div>
          </div>
        </div>
      );
    }
    
    return (
      <PremiumQuizMode
        user={user}
        onBack={() => setCurrentPage('dashboard')}
        onQuizComplete={() => {
          // Refresh dashboard by incrementing key (forces re-mount and data refetch)
          console.log('Quiz completed, refreshing dashboard...');
          setDashboardKey(prev => prev + 1);
          setCurrentPage('dashboard');
        }}
      />
    );
  }

  // Exam Mode Page  
  if (currentPage === 'exam') {
    return (
      <ExamMode
        user={user}
        onBack={() => setCurrentPage('dashboard')}
        hasActiveSubscription={hasActiveSubscription}
        onPayment={() => setCurrentPage('payment')}
        onExamComplete={() => {
          // Refresh dashboard when exam is complete
          console.log('Exam completed, refreshing dashboard...');
          setDashboardKey(prev => prev + 1);
        }}
      />
    );
  }

  // Practice Mode Selection Page
  if (currentPage === 'practice-mode') {
    return (
      <PracticeModeSelection 
        onSelectCategory={handleStartPractice}
        onBack={() => setCurrentPage('dashboard')}
      />
    );
  }

  // Practice Page
  if (currentPage === 'practice') {
    if (showResult) {
      const results = calculateResults();
      return (
        <div className="min-h-screen bg-gradient-to-br from-red-950 via-red-800 to-red-900 py-12">
          <div className="container mx-auto px-4 max-w-6xl">
            <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl p-8 shadow-2xl">
              <h2 className="text-5xl font-black text-white text-center mb-12">
                Sonuçlar
              </h2>
              
              {/* Score Card */}
              <div className="relative bg-gradient-to-br from-red-600 via-red-500 to-white rounded-3xl p-10 text-white mb-10 overflow-hidden shadow-2xl">
                <div className="absolute inset-0 bg-white/10 backdrop-blur-sm"></div>
                <div className="relative z-10">
                  <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-32 h-32 bg-white/20 backdrop-blur-xl rounded-full mb-4">
                      <div className="text-8xl font-black">{results.percentage}%</div>
                    </div>
                    <div className="text-3xl font-bold">Başarı Oranı</div>
                  </div>
                  <div className="grid grid-cols-2 gap-6">
                    <div className="bg-white/20 backdrop-blur-xl rounded-2xl p-6 text-center">
                      <CheckCircle className="w-12 h-12 mx-auto mb-3" />
                      <div className="text-5xl font-black mb-2">{results.correct}</div>
                      <div className="text-xl font-semibold">Doğru</div>
                    </div>
                    <div className="bg-white/20 backdrop-blur-xl rounded-2xl p-6 text-center">
                      <XCircle className="w-12 h-12 mx-auto mb-3" />
                      <div className="text-5xl font-black mb-2">{results.wrong}</div>
                      <div className="text-xl font-semibold">Yanlış</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Questions Review */}
              <div className="space-y-6 mb-10">
                <h3 className="text-3xl font-bold text-white mb-6 flex items-center gap-3">
                  <Lightbulb className="w-8 h-8 text-white" />
                  Soru Detayları
                </h3>
                {questions.map((question, idx) => {
                  const userAnswer = userAnswers[idx];
                  const isCorrect = userAnswer === question.correct_answer;
                  return (
                    <div key={idx} className={`bg-white/10 backdrop-blur-xl border-l-4 ${isCorrect ? 'border-green-400' : 'border-red-400'} rounded-2xl p-6`}>
                      <div className="flex items-start gap-4">
                        {isCorrect ? (
                          <CheckCircle className="w-8 h-8 text-green-400 flex-shrink-0 mt-1" />
                        ) : (
                          <XCircle className="w-8 h-8 text-red-400 flex-shrink-0 mt-1" />
                        )}
                        <div className="flex-1">
                          <h4 className="text-white font-bold mb-3">{question.question_text}</h4>
                          <div className="space-y-2 mb-3">
                            {question.choices.map((choice) => {
                              const isUserChoice = choice.letter === userAnswer;
                              const isCorrectChoice = choice.letter === question.correct_answer;
                              return (
                                <div
                                  key={choice.letter}
                                  className={`px-4 py-2 rounded-xl ${
                                    isCorrectChoice
                                      ? 'bg-green-500/20 border border-green-400/50'
                                      : isUserChoice
                                      ? 'bg-red-500/20 border border-red-400/50'
                                      : 'bg-white/5 border border-white/10'
                                  }`}
                                >
                                  <span className="font-bold text-white">{choice.letter}.</span>
                                  <span className="text-white/90 ml-2">{choice.text}</span>
                                </div>
                              );
                            })}
                          </div>
                          <div className="bg-blue-600/20 border border-blue-400/30 rounded-xl p-4">
                            <div className="text-red-300 text-sm font-semibold mb-1">Açıklama:</div>
                            <div className="text-white/80 text-sm">{question.explanation}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <button
                onClick={backToDashboard}
                className="w-full bg-gradient-to-r from-red-600 to-red-700 text-white px-8 py-4 rounded-2xl font-bold text-lg hover:shadow-2xl transition-all duration-300 hover:scale-105"
              >
                Ana Sayfaya Dön
              </button>
            </div>
          </div>
        </div>
      );
    }

    // Question Page
    const currentQuestion = questions[currentQuestionIndex];
    const currentAnswer = userAnswers[currentQuestionIndex];

    return (
      <div className="min-h-screen bg-gradient-to-br from-red-950 via-red-800 to-red-900 py-12">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl p-8 shadow-2xl">
            {/* Progress */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <span className="text-white/70 font-semibold">Soru {currentQuestionIndex + 1} / {questions.length}</span>
                <span className="text-white/70 font-semibold">{selectedCategory?.name_turkish}</span>
              </div>
              <div className="w-full bg-white/20 rounded-full h-2">
                <div
                  className="bg-gradient-to-r from-red-600 to-red-700 h-2 rounded-full transition-all"
                  style={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
                ></div>
              </div>
            </div>

            {/* Question */}
            <div className="mb-8">
              <h3 className="text-2xl font-bold text-white mb-6">{currentQuestion.question_text}</h3>
              <div className="space-y-3">
                {currentQuestion.choices.map((choice) => (
                  <button
                    key={choice.letter}
                    onClick={() => handleAnswer(choice.letter)}
                    className={`w-full text-left px-6 py-4 rounded-2xl font-semibold transition-all ${
                      currentAnswer === choice.letter
                        ? 'bg-gradient-to-r from-red-600 to-red-700 text-white shadow-lg scale-105'
                        : 'bg-white/10 text-white hover:bg-white/20 border border-white/20'
                    }`}
                  >
                    <span className="font-bold">{choice.letter}.</span>
                    <span className="ml-2">{choice.text}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Navigation */}
            <div className="flex gap-4">
              <button
                onClick={prevQuestion}
                disabled={currentQuestionIndex === 0}
                className="flex-1 bg-white/10 text-white px-6 py-3 rounded-2xl font-semibold hover:bg-white/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Önceki
              </button>
              <button
                onClick={nextQuestion}
                disabled={!currentAnswer}
                className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-3 rounded-2xl font-semibold hover:shadow-2xl transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {currentQuestionIndex === questions.length - 1 ? 'Bitir' : 'Sonraki'}
              </button>
            </div>

            <button
              onClick={backToDashboard}
              className="w-full mt-4 bg-white/10 text-white/70 hover:text-white px-6 py-3 rounded-2xl font-semibold hover:bg-white/20 transition-all"
            >
              Vazgeç
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

export default App;
